import { Link } from "react-router-dom";

export default function About() {
  return (
    <div className="page">
      <div className="stack-lg">
        <header>
          <h1 className="h1">Verified practice, by construction.</h1>
          <p className="lede">
            Every question on MoreQuestions clears a four-tier verification
            cascade before a student sees it. The answer key isn&rsquo;t a guess
            from a language model &mdash; it&rsquo;s the result of running the
            actual math.
          </p>
        </header>

        <div className="card stack-lg">
          <section>
            <h2 className="about__h2">The thesis</h2>
            <p>
              A practice question is only as good as the answer key behind it.
              A wrong key &mdash; even one wrong key &mdash; teaches the student
              the wrong thing. Worse, it erodes the trust that makes practice
              worth doing in the first place.
            </p>
            <p>
              MoreQuestions is built on a generate-then-verify loop. The
              generator drafts a question; the verifier earns it the right to
              be served. The verifier is the thesis. The generator is just
              what feeds it.
            </p>
          </section>

          <section>
            <h2 className="about__h2">The verifier cascade</h2>
            <p className="muted" style={{ marginTop: 0 }}>
              Each draft must pass all four tiers in order. A failure at any
              tier sends the draft back to the generator with a structured
              critique &mdash; not deleted, revised.
            </p>
            <ol className="about__pipeline">
              <li>
                <span className="about__tier">Tier 0 &mdash; Schema &amp; dedup</span>
                <span>
                  Malformed JSON, missing fields, illegal cognitive_type /
                  variety_axis combinations, or text near-duplicates of bank
                  questions are caught and rejected before any compute runs.
                </span>
              </li>
              <li>
                <span className="about__tier">Tier 1 &mdash; Code execution</span>
                <span>
                  The verifier reads the question&rsquo;s premises, writes a
                  short Python program that computes the answer, and compares
                  the result to what the generator wrote. If they disagree, the
                  generator&rsquo;s answer key is wrong &mdash; reject.
                </span>
              </li>
              <li>
                <span className="about__tier">Tier 2 &mdash; Pedagogy review</span>
                <span>
                  A second strong model independently evaluates the question
                  for pedagogical coherence: does it test one thing cleanly,
                  are the distractors honest, does the worked solution justify
                  the answer step-by-step? Critiques become revision targets.
                </span>
              </li>
              <li>
                <span className="about__tier">Tier 3 &mdash; Arena consensus</span>
                <span>
                  Conceptual questions go to multiple frontier models for
                  cross-agreement before reaching the bank. Disagreement at
                  this tier surfaces the rare cases where one model&rsquo;s
                  framing is subtly off.
                </span>
              </li>
            </ol>
          </section>

          <section>
            <h2 className="about__h2">Who builds the questions</h2>
            <p>
              The author is an accounting professor and textbook author. The
              question bank is generated against a concept map he authored
              and curated &mdash; not a scrape. The verifier was built and
              stress-tested before the first question was generated.
            </p>
          </section>

          <section className="about__bounty">
            <h2 className="about__h2">The bounty</h2>
            <p>
              We&rsquo;re confident enough in our verification that if you
              find a genuine error in a question or its answer key, you get a
              year free. We don&rsquo;t expect students to find errors. The
              bounty is how sure we are.
            </p>
          </section>
        </div>

        <div className="about__cta">
          <Link to="/practice" className="btn btn--lg">Start practicing</Link>
        </div>
      </div>
    </div>
  );
}
